import { type SinglePointCalculation, calculateSinglePoint } from "@suse-o11y/calculate-value-from-points/single-point";
import type { DataPoint } from "@suse-o11y/data-point";
import { dataPointsInInterval } from "@suse-o11y/data-point/data-points-in-interval";
import type { Interval } from "@suse-o11y/date-time";
import { isIntervalEqual } from "@suse-o11y/date-time/is-interval-equal";
import type { Time } from "@suse-o11y/date-time/time";
import { isShallowEqual } from "@suse-o11y/is-shallow-equal";
import type { Point } from "@suse-o11y/point";
import { useMemo } from "@suse-o11y/use-memo";
import React from "react";

import { For } from "~/components/for";
import { SkeletonBox } from "~/components/skeleton/box";

import { Segment } from "./_segment";
import { toPoint } from "./_to-point";

const DEFAULT_LINE_COLOR = "var(--dataviz-stroke-metrics-line-charts-blue-strong)";
const AXIS_LINE_COLOR = "var(--dataviz-stroke-metrics-chart-elements-gridline-solid)";
const EMPTY_LINE_COLOR = "var(--dataviz-stroke-metrics-chart-elements-gridline-dashed)";
const MARKER_BORDER_COLOR = "var(--core-layer-neutral-primary)";
const LINE_OPACITY = 0.8;
const EMPTY_LINE_OFFSET = 1;

const APPEARANCE_SIZES = {
	thin: {
		lineWidth: 1,
		markerRadius: 3,
		markerBorder: 1.5,
	},
	normal: {
		lineWidth: 2,
		markerRadius: 6,
		markerBorder: 3,
	},
} as const;

function toSegments({
	points,
	interval,
	calculation,
	padding,
	width,
	height,
}: {
	points: ReadonlyArray<DataPoint>;
	interval: Interval;
	calculation: undefined | SinglePointCalculation | Time;
	padding: number;
	width: number;
	height: number;
}): {
	segments: Array<Segment>;
	marker?: Point;
} {
	const clipped = dataPointsInInterval(points, interval);

	const convert = toPoint(clipped, {
		viewboxWidth: width,
		viewboxHeight: height,
		padding,
	});

	if (convert == null) {
		return {
			segments: [],
		};
	}

	const marker = calculation == null ? undefined : calculateSinglePoint(calculation, points);
	const bottom = height - padding;

	return {
		segments: Segment.fromDataPoints(clipped, convert, bottom),
		marker: marker == null ? undefined : convert(marker),
	};
}

type StatAreaAppearance = keyof typeof APPEARANCE_SIZES;

export interface StatAreaProps {
	className?: string;
	width: number;
	height: number;
	points: undefined | ReadonlyArray<DataPoint>;
	interval: Interval;

	/**
	 * Either calculate the value OR select from a closest to the specified time.
	 */
	calculation?: SinglePointCalculation | Time;

	/**
	 * @default "var(--dataviz-stroke-metrics-line-charts-blue-strong)"
	 */
	lineColor?: string;

	/**
	 * @default lineColor
	 */
	areaColor?: string;

	/**
	 * @default "normal"
	 */
	appearance?: StatAreaAppearance;
}

export const StatArea = React.forwardRef<React.ComponentRef<"svg">, StatAreaProps>(
	(
		{
			points,
			width,
			height,
			interval,
			calculation,
			lineColor = DEFAULT_LINE_COLOR,
			areaColor = lineColor,
			appearance = "normal",
			...props
		},
		ref,
	) => (
		<SkeletonBox
			el="svg"
			ref={ref}
			{...props}
			aria-hidden="true"
			height="100%"
			style={{ width: width || undefined, height: height || undefined }}
			viewBox={`0 0 ${width} ${height}`}
			when={width > 0 && height > 0 && points != null ? { points, width, height } : undefined}
			width="100%"
		>
			{(payload) => (
				<Chart
					{...payload}
					appearance={appearance}
					areaColor={areaColor}
					calculation={calculation}
					interval={interval}
					lineColor={lineColor}
				/>
			)}
		</SkeletonBox>
	),
);

const Chart = React.memo<{
	height: number;
	width: number;
	lineColor: string;
	areaColor: string;
	appearance: StatAreaAppearance;
	interval: Interval;
	calculation: undefined | SinglePointCalculation | Time;
	points: ReadonlyArray<DataPoint>;
}>(({ height, width, lineColor, areaColor, appearance, interval, calculation, points }) => {
	const { lineWidth, markerRadius, markerBorder } = APPEARANCE_SIZES[appearance];

	const { segments, marker } = useMemo(
		() => toSegments({ points, interval, padding: markerRadius, calculation, width, height }),
		[points, interval, calculation, markerRadius, width, height],
		[isShallowEqual, isIntervalEqual],
	);

	return (
		<>
			{/* The x-axis spans the full time window to make gaps before and after the data visible. */}
			{segments.length > 0 && (
				<line
					stroke={AXIS_LINE_COLOR}
					strokeWidth={1}
					x1={markerRadius}
					x2={width - markerRadius}
					y1={height - markerRadius}
					y2={height - markerRadius}
				/>
			)}

			<For each={segments} el={React.Fragment} empty={<Empty height={height} lineWidth={lineWidth} width={width} />}>
				{(segment) => (
					<>
						<polygon className="mask-b-from-black/35" fill={areaColor} points={segment.areaPoints.get()} />

						<polyline
							fill="none"
							points={segment.linePoints.get()}
							stroke={lineColor}
							strokeOpacity={LINE_OPACITY}
							strokeWidth={lineWidth}
						/>
					</>
				)}
			</For>

			{marker && (
				<circle
					cx={marker.x}
					cy={marker.y}
					fill={lineColor}
					r={markerRadius}
					stroke={MARKER_BORDER_COLOR}
					strokeWidth={markerBorder}
				/>
			)}
		</>
	);
});

const Empty: React.FC<{ width: number; height: number; lineWidth: number }> = ({ width, height, lineWidth }) => (
	<line
		stroke={EMPTY_LINE_COLOR}
		strokeDasharray="4 3"
		strokeWidth={lineWidth}
		x1={0}
		x2={width}
		y1={height - EMPTY_LINE_OFFSET}
		y2={height - EMPTY_LINE_OFFSET}
	/>
);
